import React, { useEffect, useState, useCallback } from "react";
import Offcanvas from "react-bootstrap/Offcanvas";
import {
  Box,
  Card,
  CircularProgress,
  Typography,
  Button,
  TextField,
  Grid,
  Autocomplete,
  Checkbox,
  MenuItem,
  Divider,
} from "@mui/material";
import { RxCross2 } from "react-icons/rx";
import { formatShortDateTime, customTextColor } from "../../utils/const";
import { useDispatch, useSelector } from "react-redux";
import {
  getHotlistChatByCandidateId,
  sendHotlistChatMessage,
} from "../../redux/hotlist/hotListRecSlice";
import { Icon } from "@iconify/react/dist/iconify";
import { getCandidate } from "../../redux/hotlist/hotListSlice";
import { allSkill } from "../../redux/common/skillSlice";
import { addLocation, allLocation } from "../../redux/common/locationSlice";
import { useFormik } from "formik";
import * as Yup from "yup";

const HotListMsgDrawer = ({
  open,
  onClose,
  candidate,
  hotlist,
  hotProfile,
}) => {
  const dispatch = useDispatch();
  const stableDispatch = useCallback(dispatch, []);
  const chartData = useSelector((state) => state.hotlistrec.chatMessages);
  const loading = useSelector(
    (state) => state.hotlistrec?.loading?.getHotlistChatByCandidateId
  );
  const profile = useSelector((state) => state.profile?.data?.profile);
  const [isExpanded, setIsExpanded] = useState(false);
  const [message, setMessage] = useState("");
  const messages = chartData?.messages || [];
  const MAX_MESSAGE_LENGTH = 300; // Maximum allowed characters for the message
  const messageLength = message.length;
  const isMessageValid =
    messageLength <= MAX_MESSAGE_LENGTH && message.trim() !== "";
  const [viewMode, setViewMode] = useState("timeline");

  const workautho = useSelector((state) => state.workautho);
  const locations = useSelector((state) => state.location) || [];
  const candidateDetails = useSelector((state) => state.hotlist);
  const skillData = useSelector((state) => state.skill.data);
  const { candidateDetail } = candidateDetails;
  const { loading: candidateLoading } = candidateDetails;

  useEffect(() => {
    if (candidate.id) {
      stableDispatch(getHotlistChatByCandidateId(candidate.id));
      stableDispatch(getCandidate(candidate.id));
      stableDispatch(allSkill());
      stableDispatch(allLocation());
    }
  }, [stableDispatch, candidate.id]);

  const displayedMessages = isExpanded ? messages : messages.slice(-2);

  const handleSendRequest = async () => {
    if (!isMessageValid) {
      return; // Do not proceed if the message is invalid
    }
    if (message.trim()) {
      const messageData = {
        candidate: candidate.id,
        receiver: hotProfile.userId,
        message: message,
      };

      try {
        await stableDispatch(sendHotlistChatMessage(messageData)).unwrap();
        setMessage("");
        setViewMode("timeline");
        stableDispatch(getHotlistChatByCandidateId(candidate.id));
      } catch (error) {
        console.error("Message sending failed:", error);
        // Optional: show error to user
      }
    }
  };

  useEffect(() => {
    if (messages.length === 0) {
      if (hotlist.status === "Published") {
        setViewMode("message");
      } else if (hotlist.status === "Shared") {
        setViewMode("job");
      } else {
        setViewMode("timeline");
      }
    } else {
      setViewMode("timeline");
    }
  }, [messages, hotlist.status]);

  const handleBack = () => {
    setMessage("");
    setViewMode("timeline");
  };

  const getLocationById = (id) => {
    const locationResult = locations?.data?.locations?.find(
      (res) => res.id === id
    );
    return locationResult?.name || "";
  };

  const getWorkAuthorization = (id) => {
    const authorizationResult = workautho?.data?.work_authorization_data?.find(
      (res) => res.id === id
    );
    return authorizationResult?.name || "";
  };

  const getSkillNamesByIds = (skillIds) => {
    return (
      skillData?.locations
        ?.filter((skill) => skillIds?.includes(skill.id))
        .map((skill) => skill.name)
        .join(", ") || ""
    );
  };

  const checkAndAddLocation = useCallback(
    async (locationName) => {
      if (
        !locationName ||
        typeof locationName !== "string" ||
        locationName.trim() === "" ||
        locationName.toLowerCase() === "undefined"
      ) {
        console.error("Invalid location name:", locationName);
        return null;
      }

      const normalizedLocationName = locationName.trim().toLowerCase();
      const existingLocation = locations?.data?.locations?.find(
        (loc) => loc.name.toLowerCase() === normalizedLocationName
      );
      if (existingLocation) {
        return existingLocation.id;
      }

      try {
        const response = await stableDispatch(
          addLocation({ name: locationName.trim() })
        ).unwrap();
        await stableDispatch(allLocation());
        return response?.id || null;
      } catch (error) {
        console.error("Error adding location:", error);
        return null;
      }
    },
    [locations?.data?.locations, stableDispatch]
  );

  const formatRateDisplay = (value) => {
    return value ? `$${value} hourly` : "";
  };

  const handleRateChange = (e) => {
    const value = e.target.value;
    const numericValue = value.replace(/[^0-9]/g, "");
    if (numericValue === "" || !isNaN(parseInt(numericValue))) {
      formik.setFieldValue("rate", numericValue ? parseInt(numericValue) : "");
    }
  };

  Yup.addMethod(Yup.string, "maxWords", function (maxWords, message) {
    return this.test("maxWords", message, function (value) {
      if (!value) return true;
      const wordCount = value.trim().split(/\s+/).length;
      return wordCount <= maxWords;
    });
  });

  const formik = useFormik({
    initialValues: {
      jobTitle: "",
      jobLocation: [],
      endClient: "",
      rate: "",
      jobDescription: "",
    },
    validationSchema: Yup.object({
      jobTitle: Yup.string().required("Job Title is required"),
      jobLocation: Yup.array()
        .of(Yup.number())
        .min(1, "At least one Job Location is required")
        .required("Job Location is required"),
      endClient: Yup.string().optional(),
      rate: Yup.number()
        .required("Rate is required")
        .positive("Rate must be a positive number"),
      jobDescription: Yup.string()
        .required("Job Description is required")
        .maxWords(1500, "Job Description cannot exceed 1500 words"),
    }),
    onSubmit: (values) => {
      const messageData = {
        candidate: candidate.id,
        receiver: hotProfile.userId,
        message: values.jobDescription,
        job_title: values.jobTitle,
        job_location: values.jobLocation,
        end_client: values.endClient,
        rate: values.rate,
      };
      stableDispatch(sendHotlistChatMessage(messageData));
      setViewMode("timeline");
      onClose();
    },
  });

  const getWordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).length;
  };

  const jobDescriptionWordCount = getWordCount(formik.values.jobDescription);

  const getPreferLocationNames = (preferLocation) => {
    return preferLocation
      ?.map(
        (id) =>
          locations?.data?.locations?.find((location) => location.id === id)
            ?.name
      ) // Map IDs to names
      ?.filter(Boolean) // Remove any undefined values if IDs don't match
      ?.join(", "); // Join names with a comma and space
  };

  return (
    <Offcanvas
      show={open}
      onHide={onClose}
      placement="end"
      scroll={false}
      backdrop={false}
      style={{
        border: "none",
        marginTop: "65px",
        width: "50%",
        boxShadow: "-4px 0 10px rgba(0, 0, 0, 0.15)",
        backgroundColor: "#fff",
      }}
    >
      <Offcanvas.Body style={{ padding: 0, margin: 0 }}>
        <Box sx={{ px: "10px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "10px",
            }}
          >
            <Typography variant="h6" style={{ color: customTextColor }}>
              Consultant - {candidate?.full_name} | {hotlist?.name}
            </Typography>
            <RxCross2
              style={{
                fontSize: "15px",
                cursor: "pointer",
                color: customTextColor,
              }}
              onClick={onClose}
            />
          </Box>

          {loading === "pending" ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
              <CircularProgress />
            </Box>
          ) : viewMode === "message" ? (
            <Box sx={{ padding: "0px 10px" }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                variant="outlined"
                placeholder="Type your message here..."
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "3px",
                  },
                }}
                error={messageLength > MAX_MESSAGE_LENGTH} // Show error if message exceeds limit
                helperText={
                  messageLength > MAX_MESSAGE_LENGTH
                    ? `Message cannot exceed ${MAX_MESSAGE_LENGTH} characters`
                    : `${messageLength}/${MAX_MESSAGE_LENGTH}`
                }
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleSendRequest}
                  disabled={!isMessageValid} // Disable if message is invalid
                  sx={{
                    backgroundColor: !isMessageValid ? "#b0bec5" : "#00b0f0", // Gray out if disabled
                    textTransform: "capitalize",
                    borderRadius: "3px",
                    padding: "5px 20px",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: !isMessageValid ? "#b0bec5" : "#0085b4",
                      boxShadow: "none",
                    },
                  }}
                >
                  Send
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  sx={{
                    textTransform: "capitalize",
                    borderRadius: "3px",
                    borderColor: "#00b0f0",
                    color: "#00b0f0",
                    "&:hover": {
                      borderColor: "#0085b4",
                      color: "#0085b4",
                    },
                  }}
                >
                  Back
                </Button>
              </Box>
            </Box>
          ) : viewMode === "job" ? (
            <Box sx={{ padding: "0px 10px" }}>
              <Box
                sx={{
                  padding: "0px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {candidateLoading?.getCandidate === "pending" && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      mt: 2,
                      py: 5,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                {candidateLoading?.getCandidate !== "pending" && (
                  <>
                    <Grid container sx={{ padding: "0px" }}>
                      <Grid item xs={6}>
                        <ul
                          style={{
                            listStyleType: "square",
                            paddingLeft: "20px",
                            margin: 0,
                          }}
                        >
                          <li>
                            <Typography style={{ fontSize: "14px" }}>
                              {candidateDetail?.full_name ||
                                candidate?.full_name}
                            </Typography>
                          </li>
                          <li>
                            <Typography style={{ fontSize: "14px" }}>
                              {candidateDetail?.total_experience < 1
                                ? `${Math.round(
                                    candidateDetail?.total_experience * 12
                                  )} Months`
                                : `${candidateDetail?.total_experience} ${
                                    candidateDetail?.total_experience === 1
                                      ? "Year"
                                      : "Years"
                                  }` || candidate?.experience}
                            </Typography>
                          </li>
                          <li>
                            <Typography style={{ fontSize: "14px" }}>
                              {candidateDetail?.email || candidate?.email}
                            </Typography>
                          </li>

                          <li>
                            <Typography style={{ fontSize: "14px" }}>
                              ${candidateDetail?.rate} Hourly
                            </Typography>
                          </li>
                          <li>
                            <Typography style={{ fontSize: "14px" }}>
                              {candidateDetail?.last_project}
                            </Typography>
                          </li>
                          <li>
                            <Typography style={{ fontSize: "14px" }}>
                              {candidateDetail?.willing_to_relocate
                                ? "Willing to Relocate"
                                : "Not Willing to Relocate" ||
                                  candidate?.availability}
                            </Typography>
                          </li>
                        </ul>
                      </Grid>

                      <Grid item xs={6}>
                        <ul
                          style={{
                            listStyleType: "square",
                            paddingLeft: "20px",
                            margin: 0,
                          }}
                        >
                          <li>
                            <Typography style={{ fontSize: "14px" }}>
                              {candidateDetail?.current_job_title ||
                                candidate?.role}
                            </Typography>
                          </li>
                          <li>
                            <Typography style={{ fontSize: "14px" }}>
                              {candidateDetail?.mobile_no || candidate?.phone}
                            </Typography>
                          </li>
                          <li>
                            <Typography style={{ fontSize: "14px" }}>
                              {(candidateDetail?.current_location &&
                                getLocationById(
                                  candidateDetail?.current_location
                                )) ||
                                candidate?.location}
                            </Typography>
                          </li>
                          <li>
                            <Typography style={{ fontSize: "14px" }}>
                              {candidateDetail?.p_tax_team ||
                                candidate?.contract ||
                                "Contract"}
                            </Typography>
                          </li>
                          <li>
                            <Typography style={{ fontSize: "14px" }}>
                              {(candidateDetail?.work_authorization &&
                                getWorkAuthorization(
                                  candidateDetail?.work_authorization
                                )) ||
                                candidate?.visa}
                            </Typography>
                          </li>
                          <li>
                            <Typography style={{ fontSize: "14px" }}>
                              {getPreferLocationNames(
                                candidateDetail?.prefer_location
                              ) || "Prefer Location n/a"}
                            </Typography>
                          </li>
                        </ul>
                      </Grid>
                    </Grid>

                    <Box sx={{ padding: "10px" }}>
                      <Typography
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          marginBottom: "5px",
                        }}
                      >
                        Primary Skills
                      </Typography>
                      <Typography style={{ fontSize: "14px" }}>
                        {getSkillNamesByIds(candidateDetail?.primary_skills)}
                      </Typography>
                    </Box>

                    <Box sx={{ padding: "10px" }}>
                      <Typography
                        style={{
                          fontSize: "14px",
                          fontWeight: "bold",
                          marginBottom: "5px",
                        }}
                      >
                        Secondary Skills
                      </Typography>
                      <Typography style={{ fontSize: "14px" }}>
                        {getSkillNamesByIds(candidateDetail?.secondary_skills)}
                      </Typography>
                    </Box>
                  </>
                )}
                <Box
                  sx={{
                    padding: "10px 15px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "3px",
                  }}
                >
                  <Typography
                    sx={{ mb: 0.5, fontWeight: "bold", fontSize: "14px" }}
                  >
                    Fill in the below details
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <TextField
                      label="Job Title"
                      name="jobTitle"
                      value={formik.values.jobTitle}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      variant="standard"
                      size="small"
                      required
                      sx={{ flex: 1 }}
                      error={
                        formik.touched.jobTitle &&
                        Boolean(formik.errors.jobTitle)
                      }
                      helperText={
                        formik.touched.jobTitle && formik.errors.jobTitle
                      }
                    />
                    <Autocomplete
                      multiple
                      id="jobLocation-autocomplete"
                      options={locations?.data?.locations || []}
                      disableCloseOnSelect
                      value={
                        locations?.data?.locations?.filter((location) =>
                          formik.values.jobLocation.includes(location.id)
                        ) || []
                      }
                      onChange={(event, value) => {
                        const selectedValues = value.map(
                          (location) => location.id
                        );
                        formik.setFieldValue("jobLocation", selectedValues);
                      }}
                      onBlur={formik.handleBlur}
                      getOptionLabel={(option) => option.name}
                      renderOption={(props, option, { selected }) => (
                        <MenuItem {...props}>
                          <Checkbox
                            checked={selected}
                            sx={{ marginRight: 0, marginLeft: 0 }}
                          />
                          <span>{option.name}</span>
                        </MenuItem>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Job Location"
                          variant="standard"
                          name="jobLocation"
                          sx={{ flex: 1 }}
                          required
                          error={
                            formik.touched.jobLocation &&
                            Boolean(formik.errors.jobLocation)
                          }
                          helperText={
                            formik.touched.jobLocation &&
                            formik.errors.jobLocation
                          }
                          onKeyPress={async (e) => {
                            if (e.key === "Enter" && e.target.value.trim()) {
                              e.preventDefault();
                              const inputValue = e.target.value.trim();
                              const locationId = await checkAndAddLocation(
                                inputValue
                              );
                              if (
                                locationId &&
                                !formik.values.jobLocation.includes(locationId)
                              ) {
                                formik.setFieldValue("jobLocation", [
                                  ...formik.values.jobLocation,
                                  locationId,
                                ]);
                                params.inputProps.ref.current.value = "";
                              }
                            }
                          }}
                        />
                      )}
                      sx={{ flex: 1 }}
                      freeSolo
                    />
                  </Box>
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <TextField
                      label="End Client (Optional)"
                      name="endClient"
                      value={formik.values.endClient}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      size="small"
                      variant="standard"
                      sx={{ flex: 1 }}
                      error={
                        formik.touched.endClient &&
                        Boolean(formik.errors.endClient)
                      }
                      helperText={
                        formik.touched.endClient && formik.errors.endClient
                      }
                    />
                    <TextField
                      label="Rate"
                      name="rate"
                      value={formatRateDisplay(formik.values.rate)}
                      onChange={handleRateChange}
                      onBlur={formik.handleBlur}
                      size="small"
                      variant="standard"
                      required
                      sx={{ flex: 1 }}
                      error={formik.touched.rate && Boolean(formik.errors.rate)}
                      helperText={formik.touched.rate && formik.errors.rate}
                    />
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  name="jobDescription"
                  value={formik.values.jobDescription}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  multiline
                  rows={5}
                  variant="outlined"
                  required
                  placeholder="Add your job description here..."
                  sx={{
                    mb: 2,
                    mt: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                    },
                  }}
                  error={
                    formik.touched.jobDescription &&
                    Boolean(formik.errors.jobDescription)
                  }
                  helperText={
                    formik.touched.jobDescription &&
                    formik.errors.jobDescription
                      ? formik.errors.jobDescription
                      : `${jobDescriptionWordCount}/1500 words`
                  }
                />
                <Box sx={{ display: "flex", gap: 2, mb: 0.5 }}>
                  <Button
                    variant="contained"
                    onClick={formik.handleSubmit}
                    sx={{
                      backgroundColor: "#00b0f0",
                      textTransform: "capitalize",
                      borderRadius: "3px",
                      padding: "5px 20px",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#0085b4",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Send Job
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{
                      textTransform: "capitalize",
                      borderRadius: "3px",
                      borderColor: "#00b0f0",
                      color: "#00b0f0",
                      "&:hover": {
                        borderColor: "#0085b4",
                        color: "#0085b4",
                      },
                    }}
                  >
                    Back
                  </Button>
                </Box>
                <Typography sx={{ mb: 1, fontSize: "12px" }}>
                  Credit may be applied on after delivered the mail.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box>
              <Card
                elevation={displayedMessages?.length === 0 ? 0 : 1}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mx: 1,
                  p: 1,
                  borderRadius: "3px",
                }}
              >
                <Box sx={{ width: "100%", padding: "0 10px" }}>
                  {displayedMessages?.map((msg, index) => (
                    <Box
                      key={msg.id}
                      sx={{
                        display: "flex",
                        mb: 2,
                        borderBottom:
                          index < displayedMessages.length - 1
                            ? "1px solid #e0e0e0"
                            : "none",
                        pb: index < displayedMessages.length - 1 ? 2 : 0,
                      }}
                    >
                      <Box sx={{ mr: 2 }}>
                        <img
                          src={
                            msg.sender.profile_picture ||
                            "https://via.placeholder.com/42"
                          }
                          alt="Sender Profile"
                          style={{
                            width: "50px",
                            height: "50px",
                            marginTop: "4px",
                            backgroundColor: "#fff",
                            objectFit: "contain",
                            borderRadius: "3px",
                          }}
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                textTransform: "capitalize",
                              }}
                            >
                              {msg.sender.full_name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#666",
                                mt: -0.2,
                              }}
                            >
                              {msg.sender.designation} -{" "}
                              {msg.sender.company_name}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "gray",
                                mt: -0.2,
                              }}
                            >
                              Ph:{" "}
                              {msg.sender.contact?.mobile_no || "Not provided"}{" "}
                              Mail:{" "}
                              {msg.sender.contact?.email || "Not provided"}
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              color: "#666",
                            }}
                          >
                            {formatShortDateTime(msg.timestamp)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            bgcolor:
                              msg?.job_title && msg.rate ? "#fafafa" : "#ffff",
                            px: msg?.job_title && msg.rate ? 2 : 0,
                            py: msg?.job_title && msg.rate ? 0.5 : 0,
                            borderRadius: "3px",

                            my: 0.5,
                          }}
                        >
                          {msg?.job_title && msg.rate && (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                mt: 0.5,
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  maxWidth: "50%",
                                }}
                              >
                                <Typography>
                                  <b>Job Title:</b>{" "}
                                  {msg.job_title ? msg.job_title : "n/a"}
                                </Typography>
                                <Typography>
                                  <b>Job Location:</b>{" "}
                                  {getPreferLocationNames(msg?.job_location) ||
                                    "n/a"}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  maxWidth: "50%",
                                }}
                              >
                                <Typography>
                                  <b>Rate:</b>{" "}
                                  {msg.rate ? `$${msg.rate} Houly` : "n/a "}
                                </Typography>
                                <Typography>
                                  <b>End Client:</b>{" "}
                                  {msg?.end_client ? msg?.end_client : "n/a"}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                          {msg?.job_title && msg.rate && <Divider />}

                          <Box
                            sx={{
                              mt: 1,
                              display: "flex",
                              justifyContent: "space-between",

                              borderRadius: "3px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {msg.message}
                            </Typography>
                            {profile.user_id === msg.sender.user_id
                              ? msg.is_read && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-end",
                                    }}
                                  >
                                    <Icon
                                      icon="charm:tick-double"
                                      style={{
                                        color: msg.is_read ? "#1e90ff" : "gray",
                                      }}
                                    />
                                  </Box>
                                )
                              : index === displayedMessages.length - 1 && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "flex-end",
                                    }}
                                  >
                                    <button
                                      style={{
                                        backgroundColor: "#1e90ff",
                                        color: "#fff",
                                        border: "none",
                                        borderRadius: "3px",
                                        padding: "2px 6px",
                                        fontSize: "8px",
                                        cursor: "default",
                                      }}
                                    >
                                      Replied
                                    </button>
                                  </Box>
                                )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                  {messages.length > 2 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "end",
                        mt: 2,
                        px: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "12px",
                          cursor: "pointer",
                          textDecoration: "underline",
                        }}
                        onClick={() => setIsExpanded(!isExpanded)}
                      >
                        {isExpanded ? "View Less" : "View More"}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Card>
              {displayedMessages?.length !== 0 &&
              hotlist.status === "Shared" ? (
                <Box sx={{ padding: "0px 10px", mt: 1 }}>
                  <Button
                    variant="contained"
                    onClick={() => setViewMode("message")}
                    sx={{
                      backgroundColor: "#00b0f0",
                      textTransform: "capitalize",
                      borderRadius: "3px",
                      padding: "5px 20px",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#0085b4",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Send Message
                  </Button>{" "}
                  <Button
                    variant="contained"
                    onClick={() => setViewMode("job")}
                    sx={{
                      backgroundColor: "#00b0f0",
                      textTransform: "capitalize",
                      borderRadius: "3px",
                      padding: "5px 20px",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#0085b4",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Send Job
                  </Button>
                </Box>
              ) : (
                displayedMessages?.length !== 0 && (
                  <Box sx={{ padding: "0px 10px", mt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => setViewMode("message")}
                      sx={{
                        backgroundColor: "#00b0f0",
                        textTransform: "capitalize",
                        borderRadius: "3px",
                        padding: "5px 20px",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#0085b4",
                          boxShadow: "none",
                        },
                      }}
                    >
                      Send Message
                    </Button>
                  </Box>
                )
              )}
            </Box>
          )}
        </Box>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default HotListMsgDrawer;
